import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type { InterviewConsentFetchFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewConsentFetchFailed';
import type { InterviewFetchFailedAction } from '@Application/Beneficiaries/Interviews/Actions/interviewFetchFailed';
import FetchInterview from '@Application/Beneficiaries/Interviews/Queries/FetchInterview';
import FetchInterviewConsent from '@Application/Beneficiaries/Interviews/Queries/FetchInterviewConsent';
import fetchInterview from '@Application/Beneficiaries/Interviews/fetchInterview';
import fetchInterviewConsent from '@Application/Beneficiaries/Interviews/fetchInterviewConsent';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import PageHeader from '@Guidelines/Layout/PageHeader';
import { theme } from '@Guidelines/Theme/theme';
import InterviewConsentConfirmationDialogs from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/Pages/InterviewConsentDetails/ConfirmationDialogs/InterviewConsentConfirmationDialogs';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton, Tag } from '@verclam/ui-kit';

// A single module-level formatter: `Intl.DateTimeFormat` construction is costly, and the instance is
// stateless and reusable across every call. `grantedAt`/`withdrawnAt` are instants (ISO 8601 with
// time, `InterviewConsentView`'s own docblock), unlike `InterviewView.conductedOn`, hence the time part.
const instantFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const traceLineStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

/**
 * Detects an `INTERVIEW_FETCH_FAILED` rejection carrying a `404`: the only status this screen
 * reacts to by leaving for the dossier (`guides/state-stores.md`'s "Ce que `useStoreQuery` ne
 * garantit pas", same shape as `BeneficiaryDetailsContent.isBeneficiaryNotFoundFailure`).
 */
const isInterviewNotFoundFailure = (candidate: unknown): candidate is InterviewFetchFailedAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	const action = candidate as Partial<InterviewFetchFailedAction>;

	return action.type === 'INTERVIEW_FETCH_FAILED' && action.payload?.status === 404;
};

/**
 * Detects an `INTERVIEW_CONSENT_FETCH_FAILED` rejection carrying a `404`. A `404` here has at
 * least four possible causes the client cannot tell apart (unknown/unassigned dossier, wrong
 * agency, unknown interview, no consent recorded yet) — `AllExceptionsFilter` already normalizes
 * every one of them under a French, displayable `message`, which this screen renders as-is rather
 * than asserting a phrase of its own ("no consent was ever recorded" would be false in three of
 * the four cases).
 */
const isInterviewConsentNotFoundFailure = (
	candidate: unknown,
): candidate is InterviewConsentFetchFailedAction => {
	if (typeof candidate !== 'object' || candidate === null) {
		return false;
	}

	const action = candidate as Partial<InterviewConsentFetchFailedAction>;

	return action.type === 'INTERVIEW_CONSENT_FETCH_FAILED' && action.payload?.status === 404;
};

type Props = {
	readonly subject: SubjectAddress;
	readonly interviewId: number;
};

/**
 * Owns both fetches and every loading/error/not-found/nominal branch of the consultation screen
 * (T7, V-26's "rappel du consentement et retrait" panel, given its own screen).
 *
 * Two independent reads, not one: `consent` alone never carries the interview's `conductedOn`
 * (`InterviewConsentPayload` has no interview date — duplicating it on the consent would be the
 * wrong owner), yet the withdrawal dialog's `confirmText` is exactly that date. Until both have
 * resolved, the screen stays on its skeleton — the withdraw button never renders without the date.
 *
 * Branch order is deliberate, and fixed: `interviewNotFound` leaves for the dossier first (this
 * screen is already navigating away, so nothing else is worth computing); past that,
 * `consentNotFoundMessage` renders the empty state with the server's own message; then a
 * genuine `error` (any status other than the two 404s already handled); only then is an absent
 * `interview`/`consent` read as still loading, never as a failure.
 */
const InterviewConsentDetailsContent: React.FC<Props> = ({ subject, interviewId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const [interviewNotFound, setInterviewNotFound] = React.useState(false);
	const [consentNotFoundMessage, setConsentNotFoundMessage] = React.useState<string | null>(null);

	const {
		data: interview,
		error: interviewError,
		refetch: refetchInterview,
	} = useStoreQuery({
		selector: (state) => state.interviewsStore.interview,
		stopOn: (value) => value !== null && value.id === interviewId,
		query: () =>
			dispatch(fetchInterview(new FetchInterview(facilityId, subject, interviewId))).catch(
				(rejection: unknown) => {
					if (isInterviewNotFoundFailure(rejection)) {
						setInterviewNotFound(true);
					}

					throw rejection;
				},
			),
		deps: [facilityId, subject, interviewId],
	});

	const {
		data: consent,
		error: consentError,
		refetch: refetchConsent,
	} = useStoreQuery({
		selector: (state) => state.interviewsStore.consent,
		stopOn: (value) => value !== null && value.interviewId === interviewId,
		query: () =>
			dispatch(
				fetchInterviewConsent(new FetchInterviewConsent(facilityId, subject, interviewId)),
			).catch((rejection: unknown) => {
				if (isInterviewConsentNotFoundFailure(rejection)) {
					setConsentNotFoundMessage(rejection.payload?.message ?? 'Consentement introuvable.');
				}

				throw rejection;
			}),
		deps: [facilityId, subject, interviewId],
	});

	React.useEffect(() => {
		if (interviewNotFound) {
			navigate(buildSubjectPath(subject));
		}
	}, [interviewNotFound, subject, navigate]);

	const handleRetry = React.useCallback(() => {
		refetchInterview();
		refetchConsent();
	}, [refetchInterview, refetchConsent]);

	if (interviewNotFound) {
		return null;
	}

	if (consentNotFoundMessage !== null) {
		return (
			<React.Fragment>
				<PageHeader eyebrow="ENTRETIENS" title="Consentement" />
				<EmptyState title="Consentement indisponible" description={consentNotFoundMessage} />
			</React.Fragment>
		);
	}

	if (interviewError !== null || consentError !== null) {
		return (
			<React.Fragment>
				<PageHeader eyebrow="ENTRETIENS" title="Consentement" />
				<ErrorState title="Impossible de charger le consentement" onRetry={handleRetry} />
			</React.Fragment>
		);
	}

	if (interview === null || consent === null) {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="3rem" animation="wave" shape="rectangle" />
				<Skeleton height="8rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	const conductedOnDisplay = interview.conductedOn !== null ? interview.conductedOn.toDisplay() : null;

	return (
		<React.Fragment>
			<PageHeader eyebrow="ENTRETIENS" title="Consentement" />

			{consent.isWithdrawn ? (
				<React.Fragment>
					<Tag value="CONSENTEMENT RETIRÉ" severity="danger" />
					<p css={traceLineStyle}>
						Retiré le{' '}
						{consent.withdrawnAt !== null ? instantFormatter.format(consent.withdrawnAt) : ''} par{' '}
						{consent.withdrawnByName}
					</p>
					<p css={traceLineStyle}>L'enregistrement audio a été supprimé.</p>
				</React.Fragment>
			) : (
				<React.Fragment>
					<Tag value="CONSENTEMENT RECUEILLI" severity="success" />
					<p css={traceLineStyle}>
						Recueilli le {instantFormatter.format(consent.grantedAt)} par {consent.grantedByName}
					</p>
					{conductedOnDisplay !== null ? (
						<InterviewConsentConfirmationDialogs
							facilityId={facilityId}
							subject={subject}
							interviewId={interviewId}
							conductedOnDisplay={conductedOnDisplay}
						/>
					) : null}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default InterviewConsentDetailsContent;
